import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

import { ChatNotificationService } from './chat-notification';

export interface JwtResponse {
  token: string;
  authentificatedUser: DecodedToken;
}

export interface LoginApiResponse {
  token?: string;
  authentificatedUser?: DecodedToken;
  mfaRequired?: boolean;
  mfaToken?: string;
}

export interface DecodedToken {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
  active?: boolean;
  profilePicture?: string;
  mfaEnabled?: boolean;
  roles: { id: number; roleName: string; name?: string; description: string }[];
  etablissements?: { id: number; etabName: string }[];
  user?: boolean;
  admin?: boolean;
  exp?: number;
}

export type AuthStepResult =
  | { mfaRequired: true; mfaToken: string }
  | { mfaRequired: false; token: string; authentificatedUser: DecodedToken };

export function isSuccessfulLogin(
  r: AuthStepResult
): r is { mfaRequired: false; token: string; authentificatedUser: DecodedToken } {
  return r.mfaRequired === false;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private loginUrl = environment.BasePath + '/authenticate';
  private mfaLoginUrl = environment.BasePath + '/authenticate/mfa';
  private unreadMessagesUrl = `${environment.BasePath}/api/chat/unread`;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private chatNotificationService: ChatNotificationService
  ) {}

  authenticate({ username, password }: { username: string; password: string }): Observable<AuthStepResult> {
    const body = { username, password };
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.httpClient.post<LoginApiResponse>(this.loginUrl, body, httpOptions).pipe(
      map((response) => {
        if (response?.mfaRequired === true && response?.mfaToken) {
          return { mfaRequired: true as const, mfaToken: response.mfaToken };
        }
        if (response?.token) {
          this.persistSuccessfulLogin(response.token, username, response.authentificatedUser);
          return {
            mfaRequired: false as const,
            token: response.token,
            authentificatedUser: response.authentificatedUser as DecodedToken
          };
        }
        throw new Error('Unexpected authentication response');
      }),
      tap((result) => {
        if (!result.mfaRequired) {
          const userId = sessionStorage.getItem('userId');
          if (userId) {
            this.fetchUnreadMessages(parseInt(userId, 10));
          }
        }
      }),
      catchError((error) => {
        console.error('Authentication failed:', error);
        return throwError(() => error);
      })
    );
  }

  completeMfaLogin(mfaToken: string, code: string, username: string): Observable<AuthStepResult> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.httpClient
      .post<LoginApiResponse>(this.mfaLoginUrl, { mfaToken, code }, httpOptions)
      .pipe(
        map((response) => {
          if (response?.token) {
            this.persistSuccessfulLogin(response.token, username, response.authentificatedUser);
            return {
              mfaRequired: false as const,
              token: response.token,
              authentificatedUser: response.authentificatedUser as DecodedToken
            };
          }
          throw new Error('Unexpected MFA response');
        }),
        tap(() => {
          const userId = sessionStorage.getItem('userId');
          if (userId) {
            this.fetchUnreadMessages(parseInt(userId, 10));
          }
        }),
        catchError((error) => throwError(() => error))
      );
  }

  private persistSuccessfulLogin(
    token: string,
    username: string,
    authentificatedUser?: DecodedToken
  ): void {
    let t = token;
    if (t.startsWith('Bearer ')) {
      t = t.replace('Bearer ', '');
    }
    sessionStorage.setItem('token', t);
    sessionStorage.setItem('username', username);

    const decodedToken = authentificatedUser || this.decodeToken(t);
    if (decodedToken) {
      sessionStorage.setItem('authUser', JSON.stringify(decodedToken));
      sessionStorage.setItem('userId', decodedToken.id?.toString() || '0');
      sessionStorage.setItem(
        'roles',
        JSON.stringify(decodedToken.roles?.map((role) => role.roleName || role.name) || [])
      );
      sessionStorage.setItem('isLogin', 'true');

      if (decodedToken.etablissements?.length) {
        sessionStorage.setItem('etablissement', JSON.stringify(decodedToken.etablissements[0]));
      }
    }
  }

  private fetchUnreadMessages(userId: number): void {
    setTimeout(() => {
      const token = this.getToken();
      if (!token) {
        console.warn('No token available to fetch unread messages');
        return;
      }

      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
      });

      this.httpClient
        .get<any[]>(`${this.unreadMessagesUrl}/${userId}`, { headers })
        .subscribe({
          next: (unreadMessages) => {
            const unreadCount = unreadMessages.filter(
              (msg) => msg.sender?.id && msg.sender.id !== userId
            ).length;
            this.chatNotificationService.setUnreadCount(unreadCount);
          },
          error: (err) => {
            console.error('Failed to fetch unread messages:', err);
          },
        });
    }, 100);
  }

  private decodeToken(token: string): DecodedToken | null {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded) as DecodedToken;
    } catch (e) {
      console.error('Failed to decode JWT token:', e);
      return null;
    }
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    const decoded = this.decodeToken(token);
    if (!decoded?.exp) return false;

    const now = Math.floor(Date.now() / 1000);
    return decoded.exp < now;
  }

  getToken(): string {
    return sessionStorage.getItem('token') || '';
  }

  getUsername(): string {
    return sessionStorage.getItem('username') || '';
  }

  getUserId(): number {
    const userId = sessionStorage.getItem('userId');
    return userId ? parseInt(userId, 10) : 0;
  }

  getRoles(): string[] {
    const roles = sessionStorage.getItem('roles');
    return roles ? JSON.parse(roles) : [];
  }

  getAuthUser(): DecodedToken | null {
    const authUser = sessionStorage.getItem('authUser');
    return authUser ? JSON.parse(authUser) : null;
  }

  isAdmin(): boolean {
    const roles = this.getRoles();
    return roles.includes('SUPER_ADMIN');
  }

  isUserLoggedIn(): boolean {
    return !!sessionStorage.getItem('username') && !this.isTokenExpired();
  }

  isEtabSelected(): boolean {
    return !!sessionStorage.getItem('etablissement');
  }

  getEtablissement(): any | null {
    const etab = sessionStorage.getItem('etablissement');
    return etab ? JSON.parse(etab) : null;
  }

  logOut(): void {
    sessionStorage.clear();
    this.chatNotificationService.setUnreadCount(0);
    this.router.navigate(['/login']);
  }
}
